import { motion } from "framer-motion";
import { useLang } from "../i18n/LangContext";
import { EditorialNumber } from "./EditorialNumber";

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const About = () => {
  const { t } = useLang();
  return (
    <section
      id="about"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-40 border-t border-[#232326]/10 overflow-hidden"
      data-testid="about-section"
    >
      <EditorialNumber n="01" variant="dark" align="right" />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{t.about.overline}</span>
            </div>
          </motion.div>

          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            custom={1}
            className="md:col-span-9 font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line"
          >
            {t.about.title}
          </motion.h2>

          {t.about.lead && (
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={2}
              className="md:col-span-9 md:col-start-4 text-[#232326]/70 text-lg leading-relaxed max-w-2xl mt-6"
            >
              {t.about.lead}
            </motion.p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-x-16 gap-y-16 md:gap-y-24 mt-8">
          {t.about.chapters.map((c, i) => (
            <motion.article
              key={c.n}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={revealUp}
              custom={i}
              className="relative pt-8"
              data-testid={`about-chapter-${c.n}`}
            >
              <div className="chapter-line h-px absolute top-0 left-0 right-0" />
              <div className="grid grid-cols-12 gap-6 md:gap-8">
                <div className="col-span-3 md:col-span-2 text-[#8A6F45] text-sm tracking-[0.2em] font-medium">
                  {c.n}
                </div>
                <div className="col-span-9 md:col-span-10">
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
                    {c.title}
                    <span className="text-[#8A6F45]">.</span>
                  </h3>
                  <p className="text-[#232326]/70 leading-relaxed max-w-md text-[0.98rem]">
                    {c.body}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
